import { type FC, type KeyboardEvent, useRef } from 'react';

import { AvailabilityBadge } from '../AvailabilityBadge';
import { Icon } from '../Icon';
import {
  cardStyles,
  descriptionStyles,
  dividerStyles,
  featureIconStyles,
  featureItemStyles,
  featureListStyles,
  featuresHeadingStyles,
  groupStyles,
  headerStyles,
  monthlyStyles,
  namePriceRowStyles,
  nativeRadioStyles,
  pinnedBadgeStyles,
  planNameStyles,
  priceAmountStyles,
  priceBlockStyles,
  pricePeriodStyles,
  radioStyles,
  titleBlockStyles,
} from './PricingSelector.styles';
import {
  type PricingSelectorPlan,
  type PricingSelectorProps,
} from './PricingSelector.types';

const wholeDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const fractionalDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatPlanPrice = (amount: number) =>
  (Number.isInteger(amount)
    ? wholeDollarFormatter
    : fractionalDollarFormatter
  ).format(amount);

const planHasDetails = (plan: PricingSelectorPlan) =>
  Boolean(plan.featuresHeading) || Boolean(plan.features?.length);

const PricingSelector: FC<PricingSelectorProps> = ({
  variant = 'compact',
  value,
  plans,
  onChange,
  className,
  name,
  ...rest
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const selectIndex = (index: number) => {
    const plan = plans[index];

    if (!plan) {
      return;
    }

    onChange(plan.id);
    inputRefs.current[index]?.focus();
  };

  const handleRadioKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Home') {
      event.preventDefault();
      selectIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      selectIndex(plans.length - 1);
    }
  };

  return (
    <div
      {...rest}
      className={className}
      css={groupStyles(variant)}
      role="radiogroup"
    >
      {plans.map((plan, index) => {
        const selected = plan.id === value;
        const showDetails = variant === 'detailed' && planHasDetails(plan);
        const price = (
          <span css={priceBlockStyles}>
            <span css={priceAmountStyles(variant)}>
              {formatPlanPrice(plan.price)}
              <span css={pricePeriodStyles}>/{plan.period}</span>
            </span>
            {variant === 'detailed' &&
            plan.period === 'yr' &&
            plan.monthlyEquivalent != null ? (
              <span css={monthlyStyles}>
                about {formatPlanPrice(plan.monthlyEquivalent)}/mo
              </span>
            ) : null}
          </span>
        );

        return (
          <label key={plan.id} css={cardStyles(selected)}>
            <input
              ref={(node) => {
                inputRefs.current[index] = node;
              }}
              css={nativeRadioStyles}
              type="radio"
              name={name}
              value={plan.id}
              checked={selected}
              onChange={() => onChange(plan.id)}
              onKeyDown={handleRadioKeyDown}
            />

            {variant === 'detailed' && plan.badge ? (
              <span css={pinnedBadgeStyles}>
                <AvailabilityBadge variant="gold">
                  {plan.badge}
                </AvailabilityBadge>
              </span>
            ) : null}

            <span css={headerStyles}>
              <span aria-hidden="true" css={radioStyles(selected)} />

              <span css={titleBlockStyles}>
                {variant === 'compact' ? (
                  <span css={namePriceRowStyles}>
                    <span css={planNameStyles(variant)}>{plan.name}</span>
                    {price}
                  </span>
                ) : (
                  <span css={planNameStyles(variant)}>{plan.name}</span>
                )}
                {variant === 'compact' && plan.badge ? (
                  <AvailabilityBadge size="sm" variant="gold">
                    {plan.badge}
                  </AvailabilityBadge>
                ) : null}

                {variant === 'compact' && plan.description ? (
                  <span css={descriptionStyles}>{plan.description}</span>
                ) : null}
              </span>

              {variant === 'detailed' ? price : null}
            </span>

            {showDetails ? (
              <>
                <hr css={dividerStyles} />
                {plan.featuresHeading ? (
                  <p css={featuresHeadingStyles}>{plan.featuresHeading}</p>
                ) : null}
                {plan.features?.length ? (
                  <ul css={featureListStyles}>
                    {plan.features.map((feature) => (
                      <li key={feature} css={featureItemStyles}>
                        <span aria-hidden="true" css={featureIconStyles}>
                          <Icon
                            variant="Check"
                            size="small"
                            fill="var(--icon-success)"
                          />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </label>
        );
      })}
    </div>
  );
};

PricingSelector.displayName = 'PricingSelector';

export default PricingSelector;
